import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Check, Building2, Crown, Sparkles, TrendingUp } from 'lucide-react';

const BecomePartner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = React.useContext(AuthContext);
  
  // Read tab from URL parameter
  const searchParams = new URLSearchParams(location.search);
  const defaultTab = searchParams.get('tab') || 'partnerships';
  
  const [partnershipPackages, setPartnershipPackages] = useState({});
  const [adPackages, setAdPackages] = useState({});
  const [contactForm, setContactForm] = useState({
    name: user?.full_name || '',
    email: user?.email || '',
    company: user?.business_name || '',
    phone: '',
    interest: 'partnership',
    message: ''
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const [partnershipsRes, adsRes] = await Promise.all([
        axios.get(`${API}/partnerships/packages`),
        axios.get(`${API}/advertisements/packages`)
      ]);
      setPartnershipPackages(partnershipsRes.data);
      setAdPackages(adsRes.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/partner/contact`, contactForm);
      toast.success('Thank you! We\'ll contact you within 24 hours.');
      setContactForm({ ...contactForm, message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    }
  };

  const handlePurchasePartnership = async (packageId) => {
    if (!user) {
      toast.error('Please login or register first');
      return;
    }
    
    try {
      toast.loading('Creating checkout session...');
      const response = await axios.post(`${API}/partnerships/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to create checkout session');
      console.error(error);
    }
  };

  const handlePurchaseAd = async (packageId) => {
    if (!user) {
      toast.error('Please login or register first');
      return;
    }
    
    // Check if user is premium or corporate member
    const isPremium = user.membership_tier === 'premium' || user.membership_tier === 'corporate';
    
    if (!isPremium) {
      toast.error('Advertising is only available to Premium and Corporate members', {
        duration: 5000,
        action: {
          label: 'Upgrade Now',
          onClick: () => navigate('/membership')
        }
      });
      return;
    }
    
    // For premium members, redirect to contact form to discuss details
    toast.info('Please use the contact form below to discuss advertising options');
    // Scroll to contact form
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Redirect to home if not logged in
  useEffect(() => {
    if (!user) {
      toast.info('Please sign up or log in to view partnership opportunities');
      navigate('/');
    }
  }, [user, navigate]);

  // If user is not logged in, show nothing while redirecting
  if (!user) {
    return null;
  }

  // Authenticated user view - show full packages with pricing
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="become-partner">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Partner With Us
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Reach thousands of ambitious women entrepreneurs and grow your brand visibility through strategic partnerships and targeted advertising.
          </p>
        </div>

        <Tabs defaultValue={defaultTab} className="space-y-8">
          <TabsList className="glass grid w-full max-w-md mx-auto grid-cols-3">
            <TabsTrigger value="partnerships">Partnerships</TabsTrigger>
            <TabsTrigger value="advertising">Advertising</TabsTrigger>
            <TabsTrigger value="contact">Contact Us</TabsTrigger>
          </TabsList>

          {/* Partnership Packages */}
          <TabsContent value="partnerships">
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="glass hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Building2 className="w-8 h-8 text-gray-600" />
                  </div>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Silver Partner</CardTitle>
                  <div className="text-3xl font-bold text-rose-600 my-4">€1,000</div>
                  <CardDescription>6 months • Best for startups</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Logo on website homepage</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">1 event sponsorship opportunity</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Basic analytics dashboard</span>
                    </li>
                  </ul>
                  <Button onClick={() => handlePurchasePartnership('silver_6months')} className="w-full bg-gray-600 hover:bg-gray-700">
                    Select Silver
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass hover:shadow-xl transition-all border-2 border-rose-500 scale-105">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-rose-500 text-white px-4 py-1 rounded-full text-sm font-semibold">Most Popular</span>
                </div>
                <CardHeader>
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                    <Crown className="w-8 h-8 text-yellow-600" />
                  </div>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Gold Partner</CardTitle>
                  <div className="text-3xl font-bold text-rose-600 my-4">€3,000</div>
                  <CardDescription>12 months • For growing companies</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Everything in Silver</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Multiple banner placements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Co-branded webinar or workshop</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Featured newsletter slot</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Detailed analytics & insights</span>
                    </li>
                  </ul>
                  <Button onClick={() => handlePurchasePartnership('gold_yearly')} className="w-full bg-rose-500 hover:bg-rose-600">
                    Select Gold
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-purple-600" />
                  </div>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Platinum Partner</CardTitle>
                  <div className="text-3xl font-bold text-rose-600 my-4">€7,500</div>
                  <CardDescription>12 months • Enterprise solution</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Everything in Gold</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Ongoing branding across all events</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Homepage & newsletter presence</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Strategic collaboration rights</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Dedicated account manager</span>
                    </li>
                  </ul>
                  <Button onClick={() => handlePurchasePartnership('platinum_yearly')} className="w-full bg-purple-600 hover:bg-purple-700">
                    Select Platinum
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Advertising Packages */}
          <TabsContent value="advertising">
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="glass hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4">
                    <TrendingUp className="w-8 h-8 text-rose-600" />
                  </div>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Featured Business</CardTitle>
                  <div className="text-3xl font-bold text-rose-600 my-4">€100/mo</div>
                  <CardDescription>30 days visibility</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Featured on homepage carousel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Priority placement in directory</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Auto-approved (instant activation)</span>
                    </li>
                  </ul>
                  <Button onClick={() => handlePurchaseAd('featured_business')} className="w-full bg-rose-500 hover:bg-rose-600">
                    Purchase Now
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <TrendingUp className="w-8 h-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Top Placement</CardTitle>
                  <div className="text-3xl font-bold text-rose-600 my-4">€100/mo</div>
                  <CardDescription>30 days top position</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Top of business directory</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Badge: "Featured Partner"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Auto-approved (instant activation)</span>
                    </li>
                  </ul>
                  <Button onClick={() => handlePurchaseAd('top_placement')} className="w-full bg-rose-500 hover:bg-rose-600">
                    Purchase Now
                  </Button>
                </CardContent>
              </Card>

              <Card className="glass hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-orange-600" />
                  </div>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Spotlight Article</CardTitle>
                  <div className="text-3xl font-bold text-rose-600 my-4">€200/mo</div>
                  <CardDescription>30 days + approval required</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Dedicated article on homepage</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Custom content with your branding</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-sm">Social media promotion included</span>
                    </li>
                  </ul>
                  <Button onClick={() => handlePurchaseAd('spotlight_article')} className="w-full bg-rose-500 hover:bg-rose-600">
                    Request Approval
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Contact Form */}
          <TabsContent value="contact" id="contact-form">
            <Card className="glass max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Get in Touch</CardTitle>
                <CardDescription>
                  Have questions or need a custom partnership? We'd love to hear from you!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Your Name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                    />
                    <Input
                      type="email"
                      placeholder="Email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      placeholder="Company Name"
                      value={contactForm.company}
                      onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                      required
                    />
                    <Input
                      placeholder="Phone (Optional)"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    />
                  </div>
                  <Select value={contactForm.interest} onValueChange={(value) => setContactForm({ ...contactForm, interest: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="I'm interested in..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="partnership">Partnership Package</SelectItem>
                      <SelectItem value="advertising">Advertising Package</SelectItem>
                      <SelectItem value="sponsorship">Event Sponsorship</SelectItem>
                      <SelectItem value="custom">Custom Collaboration</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Tell us about your goals and how you'd like to partner with us..."
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    rows={5}
                    required
                  />
                  <Button type="submit" className="w-full bg-rose-500 hover:bg-rose-600">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default BecomePartner;